import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Building } from '../types/building';
import { BuildingDataService } from '../services/buildingDataService';
import { RetrofitService } from '../services/retrofitService';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ScatterChart, Scatter } from 'recharts';

interface InternalRetrofitCalculatorProps {
  buildings: Building[];
}

const InternalRetrofitCalculator: React.FC<InternalRetrofitCalculatorProps> = ({ buildings }) => {
  const [selectedBoroughs, setSelectedBoroughs] = useState<string[]>([]);
  const [selectedBuildingTypes, setSelectedBuildingTypes] = useState<string[]>([]);
  const [minFloorArea, setMinFloorArea] = useState<number>(0);
  const [maxRetrofitCost, setMaxRetrofitCost] = useState<number>(10000000);

  const viableProspects = useMemo(() => 
    BuildingDataService.filterViableProspects(buildings),
    [buildings]
  );

  const filteredBuildings = useMemo(() => {
    return viableProspects.filter(building => {
      if (selectedBoroughs.length > 0 && !selectedBoroughs.includes(building.borough)) {
        return false;
      }
      if (selectedBuildingTypes.length > 0 && !selectedBuildingTypes.includes(building.buildingType)) {
        return false;
      }
      if (building.grossFloorArea < minFloorArea) {
        return false;
      }
      if (building.retrofitPotential && building.retrofitPotential.retrofitCost > maxRetrofitCost) {
        return false;
      }
      return true;
    });
  }, [viableProspects, selectedBoroughs, selectedBuildingTypes, minFloorArea, maxRetrofitCost]);

  const boroughs = useMemo(() => 
    Array.from(new Set(buildings.map(b => b.borough).filter(Boolean))),
    [buildings]
  );

  const buildingTypes = useMemo(() => 
    Array.from(new Set(buildings.map(b => b.buildingType).filter(Boolean))),
    [buildings]
  );

  // Chart data preparation
  const chartData = useMemo(() => {
    const boroughData = boroughs.map(borough => {
      const boroughBuildings = filteredBuildings.filter(b => b.borough === borough);
      const totalSavings = boroughBuildings.reduce((sum, b) => 
        sum + (b.costBenefitAnalysis?.totalSavingsOverLifetime || 0), 0
      );
      const totalCost = boroughBuildings.reduce((sum, b) => 
        sum + (b.retrofitPotential?.retrofitCost || 0), 0
      );
      
      return {
        borough,
        count: boroughBuildings.length,
        totalSavings: totalSavings / 1000000, // Convert to millions
        totalCost: totalCost / 1000000,
        netBenefit: (totalSavings - totalCost) / 1000000
      };
    }).filter(data => data.count > 0);

    return boroughData;
  }, [filteredBuildings, boroughs]);

  const scatterData = useMemo(() => {
    return filteredBuildings.map(building => ({
      x: building.retrofitPotential?.retrofitCost || 0,
      y: building.costBenefitAnalysis?.totalSavingsOverLifetime || 0,
      name: building.propertyName,
      payback: building.retrofitPotential?.paybackPeriod || 0
    })).filter(item => item.x > 0 && item.y > 0);
  }, [filteredBuildings]);

  const handleBoroughChange = (borough: string) => {
    setSelectedBoroughs(prev => 
      prev.includes(borough) 
        ? prev.filter(b => b !== borough)
        : [...prev, borough]
    );
  };

  const handleBuildingTypeChange = (type: string) => {
    setSelectedBuildingTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const totalPotentialSavings = filteredBuildings.reduce((sum, b) => 
    sum + (b.costBenefitAnalysis?.totalSavingsOverLifetime || 0), 0
  );
  
  const totalRetrofitCosts = filteredBuildings.reduce((sum, b) => 
    sum + (b.retrofitPotential?.retrofitCost || 0), 0
  );

  if (buildings.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-900 via-neutral-900 to-secondary-900 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-primary-200 p-8">
            {/* Professional Header with Security Badge */}
            <div className="text-center mb-8">
              <div className="flex justify-between items-start mb-6">
                <div className="flex-1">
                  <h2 className="text-4xl font-bold text-transparent bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text mb-4">
                    Professional Retrofit Investment Calculator
                  </h2>
                  <div className="flex items-center justify-center gap-4 mb-6">
                    <div className="flex items-center gap-2 px-4 py-2 bg-primary-100 rounded-full">
                      <div className="w-2 h-2 bg-primary-500 rounded-full animate-pulse"></div>
                      <span className="text-sm font-medium text-primary-700">Internal Tools</span>
                    </div>
                    <div className="flex items-center gap-2 px-4 py-2 bg-secondary-100 rounded-full">
                      <div className="w-2 h-2 bg-secondary-500 rounded-full animate-pulse"></div>
                      <span className="text-sm font-medium text-secondary-700">Professional Analytics</span>
                    </div>
                  </div>
                </div>
                <Link 
                  to="/retrofit-calculator" 
                  className="flex items-center gap-2 px-4 py-2 bg-neutral-100 hover:bg-neutral-200 rounded-lg transition-colors text-sm font-medium text-neutral-700"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Public Demo
                </Link>
              </div>

              {/* Professional Security Banner */}
              <div className="bg-gradient-to-r from-accent-500 to-accent-600 border border-accent-300 rounded-xl p-6 mb-8">
                <div className="flex items-center justify-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-white mb-2">
                      Internal Professional Tools
                    </h3>
                    <p className="text-accent-100 text-sm">
                      Advanced filtering, detailed ROI analysis, cost projections, and investment optimization tools for professional use.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Professional Empty State */}
            <div className="text-center py-12">
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-primary-100 to-secondary-100 rounded-2xl flex items-center justify-center">
                <svg className="w-12 h-12 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 00-2-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-neutral-900 mb-3">Upload Professional Building Data</h3>
              <p className="text-neutral-600 mb-8 max-w-lg mx-auto">
                Access comprehensive professional analysis tools including advanced filtering, detailed ROI calculations, market segmentation, and investment optimization features.
              </p>
              
              {/* Professional Features Preview */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gradient-to-br from-primary-50 to-primary-100 p-6 rounded-xl border border-primary-200">
                  <div className="w-12 h-12 bg-primary-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
                    </svg>
                  </div>
                  <h4 className="font-bold text-primary-900 mb-2">Advanced Filtering</h4>
                  <p className="text-sm text-primary-700">Borough, building type, size, and investment criteria filtering</p>
                </div>
                
                <div className="bg-gradient-to-br from-secondary-50 to-secondary-100 p-6 rounded-xl border border-secondary-200">
                  <div className="w-12 h-12 bg-secondary-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                  <h4 className="font-bold text-secondary-900 mb-2">ROI Analytics</h4>
                  <p className="text-sm text-secondary-700">Detailed return on investment and payback analysis</p>
                </div>
                
                <div className="bg-gradient-to-br from-accent-50 to-accent-100 p-6 rounded-xl border border-accent-200">
                  <div className="w-12 h-12 bg-accent-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  </div>
                  <h4 className="font-bold text-accent-900 mb-2">Investment Optimization</h4>
                  <p className="text-sm text-accent-700">Portfolio optimization and market opportunity analysis</p>
                </div>
              </div>

              <button className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-semibold rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all duration-200 transform hover:scale-105 shadow-xl hover:shadow-2xl">
                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                Upload Professional Dataset
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-900 via-neutral-900 to-secondary-900 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-primary-200 p-8">
          {/* Professional Header with Navigation */}
          <div className="flex justify-between items-start mb-8">
            <div className="flex-1">
              <h2 className="text-5xl font-bold text-transparent bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text mb-4">
                Professional Retrofit Investment Analysis
              </h2>
              <p className="text-lg text-neutral-600 mb-6 max-w-3xl">
                Advanced professional tools for detailed market analysis, investment optimization, and portfolio performance tracking.
              </p>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 px-4 py-2 bg-primary-100 rounded-full">
                  <div className="w-2 h-2 bg-primary-500 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium text-primary-700">Internal Tools</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-accent-100 rounded-full">
                  <div className="w-2 h-2 bg-accent-500 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium text-accent-700">Professional Analytics</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-secondary-100 rounded-full">
                  <div className="w-2 h-2 bg-secondary-500 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium text-secondary-700">Investment Optimization</span>
                </div>
              </div>
            </div>
            <Link 
              to="/retrofit-calculator" 
              className="flex items-center gap-2 px-6 py-3 bg-neutral-100 hover:bg-neutral-200 rounded-xl transition-all duration-200 text-sm font-semibold text-neutral-700 shadow-lg hover:shadow-xl"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Public Demo
            </Link>
          </div>

          {/* Professional Security Banner */}
          <div className="bg-gradient-to-r from-accent-500 to-accent-600 border border-accent-300 rounded-xl p-6 mb-8">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-white mb-2">
                  Internal Professional Tools
                </h3>
                <p className="text-accent-100 text-sm">
                  Advanced filtering, detailed ROI analysis, cost projections, and investment optimization tools for professional market analysis.
                </p>
              </div>
              <div className="text-right">
                <div className="text-xs text-accent-200 font-medium">Access Level</div>
                <div className="text-white font-bold">Professional</div>
              </div>
            </div>
          </div>

          {/* Professional Advanced Filters */}
          <div className="bg-gradient-to-br from-neutral-50 to-neutral-100 border border-neutral-200 rounded-2xl p-6 mb-8 shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-transparent bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text">
                Advanced Professional Filters
              </h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H7m2 0v-6a2 2 0 012-2h2a2 2 0 012 2v6z" />
                  </svg>
                  <label className="text-lg font-semibold text-neutral-800">Property Size</label>
                </div>
                {['Small (Under 25,000 sqft)', 'Medium (25,000-100,000 sqft)', 'Large (Over 100,000 sqft)'].map((size) => (
                  <div key={size} className="flex items-center p-3 bg-white rounded-xl border border-neutral-200 hover:border-primary-300 hover:bg-primary-50 transition-all duration-200">
                    <input
                      type="checkbox"
                      className="w-5 h-5 mr-3 rounded-lg border-neutral-300 text-primary-600 focus:ring-primary-500 focus:ring-2"
                      defaultChecked
                    />
                    <span className="text-sm font-medium text-neutral-700 hover:text-primary-700">{size}</span>
                  </div>
                ))}
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5 text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                  </svg>
                  <label className="text-lg font-semibold text-neutral-800">Building Type</label>
                </div>
                {['Office', 'Multifamily', 'Retail', 'Mixed Use', 'Industrial'].map((type) => (
                  <div key={type} className="flex items-center p-3 bg-white rounded-xl border border-neutral-200 hover:border-secondary-300 hover:bg-secondary-50 transition-all duration-200">
                    <input
                      type="checkbox"
                      className="w-5 h-5 mr-3 rounded-lg border-neutral-300 text-secondary-600 focus:ring-secondary-500 focus:ring-2"
                      defaultChecked
                    />
                    <span className="text-sm font-medium text-neutral-700 hover:text-secondary-700">{type}</span>
                  </div>
                ))}
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5 text-accent-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                  <label className="text-lg font-semibold text-neutral-800">Target ROI (%)</label>
                </div>
                <div className="relative">
                  <input
                    type="number"
                    placeholder="15"
                    className="w-full px-4 py-3 pr-12 bg-white border border-neutral-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent transition-all duration-200 font-semibold text-neutral-800"
                  />
                  <div className="absolute inset-y-0 right-0 pr-4 flex items-center">
                    <span className="text-accent-600 font-bold">%</span>
                  </div>
                </div>
                <p className="text-xs text-neutral-500">Minimum annual return expected</p>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                  <label className="text-lg font-semibold text-neutral-800">Max Investment</label>
                </div>
                <div className="relative">
                  <input
                    type="number"
                    placeholder="500000"
                    className="w-full px-4 py-3 pl-8 bg-white border border-neutral-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 font-semibold text-neutral-800"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                    <span className="text-primary-600 font-bold">$</span>
                  </div>
                </div>
                <p className="text-xs text-neutral-500">Maximum capital per property</p>
              </div>
            </div>
          </div>

          {/* Enhanced Professional Summary Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="group bg-gradient-to-br from-primary-500 to-primary-600 p-6 rounded-2xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 cursor-pointer">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H7m2 0v-6a2 2 0 012-2h2a2 2 0 012 2v6z" />
                  </svg>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-white group-hover:scale-110 transition-transform duration-200">
                    {filteredBuildings.length}
                  </div>
                  <div className="text-primary-100 text-sm font-medium">Viable Properties</div>
                </div>
              </div>
              <div className="text-primary-100 text-xs">Investment opportunities identified</div>
            </div>
            
            <div className="group bg-gradient-to-br from-secondary-500 to-secondary-600 p-6 rounded-2xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 cursor-pointer">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-white group-hover:scale-110 transition-transform duration-200">
                    formatCurrency((totalPotentialSavings / 1000000))
                  </div>
                  <div className="text-secondary-100 text-sm font-medium">10-Year Savings</div>
                </div>
              </div>
              <div className="text-secondary-100 text-xs">Total LL97 fine reductions</div>
            </div>
            
            <div className="group bg-gradient-to-br from-accent-500 to-accent-600 p-6 rounded-2xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 cursor-pointer">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-white group-hover:scale-110 transition-transform duration-200">
                    formatCurrency((totalRetrofitCosts / 1000000))
                  </div>
                  <div className="text-accent-100 text-sm font-medium">Investment Required</div>
                </div>
              </div>
              <div className="text-accent-100 text-xs">Total capital deployment</div>
            </div>
            
            <div className="group bg-gradient-to-br from-emerald-500 to-emerald-600 p-6 rounded-2xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 cursor-pointer">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-white group-hover:scale-110 transition-transform duration-200">
                    formatCurrency(((totalPotentialSavings - totalRetrofitCosts) / 1000000))
                  </div>
                  <div className="text-emerald-100 text-sm font-medium">Net Benefit</div>
                </div>
              </div>
              <div className="text-emerald-100 text-xs">Total market opportunity</div>
            </div>
          </div>

          {/* Enhanced Professional Charts */}
          <div className="space-y-8">
            {/* Borough Analysis Chart */}
            <div className="bg-gradient-to-br from-white to-neutral-50 border border-neutral-200 rounded-2xl p-6 shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 00-2-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-transparent bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text">
                  Investment Opportunity by Borough
                </h3>
                <p className="text-neutral-600 text-sm">Analyze potential savings and costs across boroughs</p>
              </div>
            </div>
            <div className="h-80 bg-white rounded-xl p-4 border border-neutral-100">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="borough" 
                    tick={{ fill: '#6b7280', fontSize: 12, fontWeight: 500 }}
                    axisLine={{ stroke: '#d1d5db' }}
                  />
                  <YAxis 
                    label={{ value: 'Amount ($M)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#6b7280', fontWeight: 500 } }}
                    tick={{ fill: '#6b7280', fontSize: 12, fontWeight: 500 }}
                    axisLine={{ stroke: '#d1d5db' }}
                  />
                  <Tooltip 
                    formatter={(value: any, name: string) => [
                      `$${Number(value).toFixed(1)}M`, 
                      name === 'totalSavings' ? 'Total Savings' :
                      name === 'totalCost' ? 'Total Cost' : 'Net Benefit'
                    ]}
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '12px',
                      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                      fontWeight: 500
                    }}
                  />
                  <Legend 
                    wrapperStyle={{ paddingTop: '20px', fontWeight: 500 }}
                  />
                  <Bar dataKey="totalSavings" fill="url(#gradientSavings)" name="Total Savings" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="totalCost" fill="url(#gradientCost)" name="Total Cost" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="netBenefit" fill="url(#gradientBenefit)" name="Net Benefit" radius={[4, 4, 0, 0]} />
                  <defs>
                    <linearGradient id="gradientSavings" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0b6e4f" stopOpacity={0.8}/>
                      <stop offset="100%" stopColor="#0b6e4f" stopOpacity={0.6}/>
                    </linearGradient>
                    <linearGradient id="gradientCost" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ff5a4c" stopOpacity={0.8}/>
                      <stop offset="100%" stopColor="#ff5a4c" stopOpacity={0.6}/>
                    </linearGradient>
                    <linearGradient id="gradientBenefit" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#002b45" stopOpacity={0.8}/>
                      <stop offset="100%" stopColor="#002b45" stopOpacity={0.6}/>
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Enhanced Cost vs Savings Scatter Plot */}
            <div className="bg-gradient-to-br from-white to-neutral-50 border border-neutral-200 rounded-2xl p-6 shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-transparent bg-gradient-to-r from-secondary-600 to-accent-600 bg-clip-text">
                    Professional ROI Analysis
                  </h3>
                  <p className="text-neutral-600 text-sm">Retrofit Cost vs. 10-Year Savings Performance Matrix</p>
                </div>
              </div>
              <div className="h-80 bg-white rounded-xl p-4 border border-neutral-100">
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart data={scatterData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="x" 
                      type="number" 
                      domain={['dataMin', 'dataMax']}
                      label={{ value: 'Retrofit Cost ($)', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fill: '#6b7280', fontWeight: 500 } }}
                      tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`}
                      tick={{ fill: '#6b7280', fontSize: 12, fontWeight: 500 }}
                      axisLine={{ stroke: '#d1d5db' }}
                    />
                    <YAxis 
                      dataKey="y" 
                      type="number" 
                      domain={['dataMin', 'dataMax']}
                      label={{ value: '10-Year Savings ($)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#6b7280', fontWeight: 500 } }}
                      tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`}
                      tick={{ fill: '#6b7280', fontSize: 12, fontWeight: 500 }}
                      axisLine={{ stroke: '#d1d5db' }}
                    />
                    <Tooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload.length > 0) {
                          const data = payload[0].payload;
                          return (
                            <div className="bg-white p-4 border border-neutral-200 rounded-xl shadow-xl">
                              <p className="font-bold text-neutral-800 mb-2">{data.name}</p>
                              <div className="space-y-1 text-sm">
                                <p className="text-accent-600"><span className="font-medium">Cost:</span> formatCurrency((data.x / 1000000))</p>
                                <p className="text-secondary-600"><span className="font-medium">Savings:</span> formatCurrency((data.y / 1000000))</p>
                                <p className="text-primary-600"><span className="font-medium">Payback:</span> {data.payback.toFixed(1)} years</p>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Scatter dataKey="y" fill="url(#scatterGradient)" />
                    
                    {/* Enhanced Break-even line */}
                    <Scatter 
                      data={[
                        { x: 0, y: 0 },
                        { x: Math.max(...scatterData.map(d => d.x)), y: Math.max(...scatterData.map(d => d.x)) }
                      ]} 
                      fill="none" 
                      stroke="#ff5a4c" 
                      strokeDasharray="8,4"
                      strokeWidth={2}
                    />
                    <defs>
                      <linearGradient id="scatterGradient" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#002b45" stopOpacity={0.8}/>
                        <stop offset="100%" stopColor="#0b6e4f" stopOpacity={0.6}/>
                      </linearGradient>
                    </defs>
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 flex items-center gap-2 text-sm text-neutral-600 bg-neutral-50 p-3 rounded-xl">
                <svg className="w-4 h-4 text-accent-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Properties above the red dashed line indicate profitable investments. Higher positions represent better ROI performance.</span>
              </div>
            </div>
          </div>

          {/* Enhanced Professional Insights */}
          <div className="mt-8 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 border border-blue-200 rounded-2xl p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-transparent bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text">
                Professional Investment Analysis
              </h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h4 className="text-lg font-bold text-blue-900 mb-3">Market Metrics</h4>
                <div className="space-y-3 text-sm text-blue-800">
                  <div className="flex items-center gap-3 p-3 bg-white/70 rounded-xl">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span><span className="font-semibold">{filteredBuildings.length} buildings</span> show viable retrofit opportunities</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-white/70 rounded-xl">
                    <div className="w-2 h-2 bg-accent-500 rounded-full"></div>
                    <span>Total market size: <span className="font-semibold">formatCurrency((totalRetrofitCosts / 1000000))</span> in retrofit investments</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-white/70 rounded-xl">
                    <div className="w-2 h-2 bg-secondary-500 rounded-full"></div>
                    <span>Expected savings: <span className="font-semibold">formatCurrency((totalPotentialSavings / 1000000))</span> from LL97 fine reductions</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-white/70 rounded-xl">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                    <span>Net owner benefit: <span className="font-semibold">formatCurrency(((totalPotentialSavings - totalRetrofitCosts) / 1000000))</span></span>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <h4 className="text-lg font-bold text-purple-900 mb-3">Performance Indicators</h4>
                <div className="space-y-3 text-sm text-purple-800">
                  {filteredBuildings.length > 0 && (
                    <div className="flex items-center gap-3 p-3 bg-white/70 rounded-xl">
                      <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      <span>Average payback: <span className="font-semibold">{(filteredBuildings.reduce((sum, b) => 
                        sum + (b.retrofitPotential?.paybackPeriod || 0), 0) / filteredBuildings.length).toFixed(1)} years</span></span>
                    </div>
                  )}
                  <div className="flex items-center gap-3 p-3 bg-white/70 rounded-xl">
                    <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                    <span>Profitable projects: <span className="font-semibold">{(scatterData.filter(d => d.y > d.x).length / scatterData.length * 100).toFixed(0)}%</span> show positive ROI</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-white/70 rounded-xl">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span>Avg investment per building: <span className="font-semibold">${(totalRetrofitCosts / filteredBuildings.length / 1000).toFixed(0)}K</span></span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-white/70 rounded-xl">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span>Top market: <span className="font-semibold">{chartData.reduce((max, curr) => curr.netBenefit > max.netBenefit ? curr : max, chartData[0])?.borough}</span> Borough</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Professional Action Items */}
          <div className="mt-8 bg-gradient-to-br from-primary-600 via-primary-700 to-secondary-700 rounded-2xl p-8 text-white shadow-2xl">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h3 className="text-3xl font-bold mb-2">
                  Professional Development Opportunities
                </h3>
                <p className="text-primary-100">Strategic investment guidance and market insights</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 bg-accent-500 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold">Investment Strategy</h4>
                </div>
                <ul className="space-y-3 text-primary-100">
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-accent-400 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Focus on buildings with &lt;5 year payback periods for optimal returns</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-accent-400 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Prioritize high-savings properties to maximize portfolio impact</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-accent-400 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Consider portfolio bundling for enhanced financing opportunities</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 bg-secondary-400 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold">Market Insights</h4>
                </div>
                <ul className="space-y-3 text-primary-100">
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Strong ROI potential concentrated in {chartData.reduce((max, curr) => curr.netBenefit > max.netBenefit ? curr : max, chartData[0])?.borough} Borough</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full mt-2 flex-shrink-0"></div>
                    <span>{(scatterData.filter(d => d.y > d.x).length / scatterData.length * 100).toFixed(0)}% of analyzed projects demonstrate positive ROI performance</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Average market size per building: ${(totalRetrofitCosts / filteredBuildings.length / 1000).toFixed(0)}K investment opportunity</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <button className="flex-1 bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 rounded-xl px-6 py-4 font-semibold transition-all duration-200 hover:scale-105">
                Download Professional Report
              </button>
              <button className="flex-1 bg-accent-500 hover:bg-accent-600 rounded-xl px-6 py-4 font-semibold transition-all duration-200 hover:scale-105 shadow-xl">
                Schedule Investment Consultation
              </button>
            </div>
          </div>

          {/* Enhanced Footer Link */}
          <div className="mt-8 text-center">
            <Link 
              to="/retrofit-calculator" 
              className="inline-flex items-center gap-2 text-neutral-500 hover:text-primary-600 transition-all duration-200 font-medium group"
            >
              <svg className="w-4 h-4 transition-transform duration-200 group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Return to Public Demo Version
            </Link>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export default InternalRetrofitCalculator;
